import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api, { tokenManager } from '../services/api';
import type { ApiResponse } from '../services/apiClient';
import { getTenantSubdomain } from '../services/apiClient';
import { extractLockedUntilIso } from '../lib/loginLockout';

/**
 * Derive the base domain a tenant subdomain should sit under, from the CURRENT
 * host — so the redirect works across environments without hard-coding a domain:
 *   saas.localhost          → "localhost"        (dev)
 *   localhost               → "localhost"        (dev, no subdomain yet)
 *   saas.esg.datacaffe.ai   → "esg.datacaffe.ai" (prod)
 *   acme.esgcaffe.ai        → "esgcaffe.ai"
 * Returns null for raw IP hosts, which can't carry a tenant subdomain.
 */
function tenantBaseDomain(): string | null {
  const host = window.location.hostname;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return null; // IPs can't host subdomains
  if (host === 'localhost') return 'localhost';
  const parts = host.split('.');
  // Dev: {label}.localhost — base is always 'localhost'
  if (parts.length === 2 && parts[1] === 'localhost') return 'localhost';
  // Real domain with a subdomain slot (sub.domain.tld): strip the leading label
  if (parts.length >= 3) return parts.slice(1).join('.');
  // Bare registrable domain (domain.tld): tenant becomes its first subdomain
  return host;
}

/**
 * After login, if the authenticated user's company has a subdomain and we're not
 * already on it, redirect to {company-subdomain}.{base-domain} — preserving the
 * current protocol and port so it works on localhost dev and any deployed domain.
 * Returns true if a redirect was initiated (caller should stop further work).
 */
function maybeRedirectToTenantSubdomain(companySubdomain?: string | null): boolean {
  const currentSubdomain = getTenantSubdomain();
  console.log(`[maybeRedirectToTenantSubdomain] called: companySubdomain="${companySubdomain}", currentSubdomain="${currentSubdomain}", host="${window.location.hostname}"`);

  if (!companySubdomain) {
    console.log('[maybeRedirectToTenantSubdomain] no companySubdomain → no redirect');
    return false;
  }
  const target = companySubdomain.trim().toLowerCase();
  if (!target) {
    console.log('[maybeRedirectToTenantSubdomain] empty target → no redirect');
    return false;
  }

  // Already on the tenant's own subdomain → nothing to do.
  if (currentSubdomain === target) {
    console.log(`[maybeRedirectToTenantSubdomain] already on correct subdomain "${target}" → no redirect`);
    return false;
  }

  // Preserve the access token via URL fragment so the target subdomain can pick it up.
  // Using fragment (#) instead of query so it doesn't hit server logs / referrers.
  const token = tokenManager.getToken();
  const fragment = token ? `#t=${encodeURIComponent(token)}` : '';

  const host = window.location.hostname;

  // Production: esgcaffe.ai-style hosts → https + tenant domain suffix.
  const TENANT_DOMAIN = import.meta.env.VITE_TENANT_DOMAIN || '.esgcaffe.ai';
  if (host.endsWith(TENANT_DOMAIN)) {
    const redirectUrl = `https://${target}${TENANT_DOMAIN}/dashboard${fragment}`;
    console.log(`[maybeRedirectToTenantSubdomain] REDIRECTING (prod) → "${redirectUrl}"`);
    window.location.href = redirectUrl;
    return true;
  }

  // Local dev: *.localhost (Chrome resolves *.localhost to 127.0.0.1).
  if (host === 'localhost' || host.endsWith('.localhost')) {
    const port = window.location.port ? `:${window.location.port}` : '';
    const redirectUrl = `${window.location.protocol}//${target}.localhost${port}/dashboard${fragment}`;
    console.log(`[maybeRedirectToTenantSubdomain] REDIRECTING (localhost) → "${redirectUrl}"`);
    window.location.href = redirectUrl;
    return true;
  }

  // IPs or other unknown hosts can't form a tenant subdomain — skip.
  console.log('[maybeRedirectToTenantSubdomain] unknown host type → no redirect');
  return false;
}

function apiErrorText(message: unknown, fallback: string): string {
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) return message.map(String).join(', ');
  return fallback;
}

function throwFailedAuth(response: ApiResponse, fallbackMessage: string): never {
  const err = new Error(apiErrorText(response.message, fallbackMessage)) as Error & {
    statusCode?: number;
    lockedUntil?: string;
    authErrorPayload?: unknown;
  };
  const raw = response.statusCode as number | string | undefined;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number.parseInt(raw, 10) : NaN;
  err.statusCode = Number.isFinite(n) ? n : undefined;
  err.authErrorPayload = response.data;
  const lockIso = extractLockedUntilIso(response.data);
  if (lockIso) err.lockedUntil = lockIso;
  throw err;
}

// Hierarchical permissions structure from role_master
export interface RolePermissions {
  [menuKey: string]: {
    [featureKey: string]: {
      label: string;
      children?: {
        [childKey: string]: string;
      };
    };
  } | {
    label: string;
    children?: {
      [childKey: string]: string;
    };
  };
}

// Menu structure from pricing plan
export interface AllowedMenu {
  id: string;
  label: string;
  path: string;
  parentId: string | null;
  icon: string | null;
  sortOrder: number;
  features: Record<string, unknown>;
  categoryId?: string | null;  // Optional: Analytic category ID for filtering charts
  routeKey?: string | null;    // Optional: super-admin componentKey — fallback when the path does not resolve
  /** Tenant's own display name (Profile → Menu Setup). Display only; `label` stays the key. */
  customLabel?: string | null;
  children?: AllowedMenu[];
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  roleId?: string;
  jobTitle?: string;
  avatar?: string;
  companyId: string;
  tenantId?: string;
  invitedBy?: string;
  company?: {
    id: string;
    name: string;
    industry: string;
    country?: string;
    currency?: string;
    subdomain?: string | null;
    /** Company logo URL. Already returned by /auth/profile (the whole Company
     *  relation is spread into the response) — it was simply never typed here,
     *  so nothing rendered it. */
    logo?: string | null;
  };
  rolePermissions?: RolePermissions;
  allowedMenus?: AllowedMenu[];
  /** Plan theme colours from super-admin (null = ESG Default). Applied by PlanThemeApplier. */
  planTheme?: import('@/components/PlanThemeApplier').PlanThemePayload | null;
}

export type LoginStepResult =
  | { requiresOtp: true; challengeId: string }
  | { requiresOtp: false };

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginStepResult>;
  verifyLoginOtp: (challengeId: string, otp: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  hasMenuPermission: (menuPath: string) => boolean;
  hasFeaturePermission: (menuPath: string, featureKey: string) => boolean;
  isInvitedUser: () => boolean;
  isMenuAllowedByPlan: (menuPath: string, isParentSection?: boolean) => boolean;
  getAllowedMenus: () => AllowedMenu[];
  getFirstAllowedMenuPath: () => string;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Title case a name string: "sanyami" → "Sanyami", "JaiN" → "Jain" */
const titleCase = (s?: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

/** Normalize user names to Title Case */
const normalizeUser = (u: User): User => ({
  ...u,
  firstName: titleCase(u.firstName),
  lastName: titleCase(u.lastName),
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserRaw] = useState<User | null>(null);
  const setUser = (u: User | null) => setUserRaw(u ? normalizeUser(u) : null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Cross-subdomain token handoff: if we were redirected from another subdomain
    // with "#t=<token>" in the URL, stash it in localStorage and strip the fragment.
    if (window.location.hash && window.location.hash.startsWith('#t=')) {
      const incomingToken = decodeURIComponent(window.location.hash.slice(3));
      if (incomingToken) {
        tokenManager.setToken(incomingToken);
        // Remove the fragment from the URL without reloading
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    // Skip auth check on public pages (landing, login, etc.) to avoid unnecessary API calls.
    // Includes every standalone tokenized public route (App.tsx) — an already-logged-in
    // visitor must NOT be workspace-redirected away from these; they render entirely on
    // their own (own fetch calls, own token) regardless of the visitor's own auth state.
    const publicPaths = ['/', '/login', '/login/verify-otp', '/forgot-password', '/reset-password', '/accept-invitation'];
    const publicPathPrefixes = [
      '/survey/',
      '/supplier-survey/',
      '/pcf-request/',
      '/guest/data/',
      '/verifier/',
      '/pcf-partner-request/',
      '/pcf-supplier-response/',
      // The CCTS monitoring-plan review link. Its route was added to App.tsx
      // but never here, so an already-logged-in operator opening the link ran
      // the auth check, which calls maybeRedirectToTenantSubdomain — and that
      // redirects to /dashboard, discarding the path. The verifier page never
      // got a chance to render; the ESG dashboard appeared instead.
      '/ccts/plan-review/',
      // The Sec 5 evidence pack link. Same reason as above: without this entry
      // an already-logged-in operator opening the verifier link is redirected
      // to /dashboard and the pack never renders.
      '/ccts/evidence-pack/',
    ];
    const isPublicPage = publicPaths.includes(window.location.pathname) || publicPathPrefixes.some((p) => window.location.pathname.startsWith(p));
    if (isPublicPage) {
      setIsLoading(false);
      return;
    }
    const token = tokenManager.getToken();
    if (token) {
      loadUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Enforce single active tenant session per browser: if another tab logs in
  // (or out), reload this tab so it adopts the most recent session.
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'token' || e.oldValue === e.newValue) return;
      window.location.reload();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Federated MFEs (e.g. settings-mfe's Profile page) run their own separate
  // AuthProvider instance, so calling their local updateUser() after an avatar
  // upload never reaches the shell's sidebar — only a full page reload picked
  // it up before. MFEs dispatch this window event (same document, so it's
  // visible across module-federation boundaries) with the fields that changed;
  // merge them into the shell's own user state + localStorage cache in place.
  useEffect(() => {
    const handleProfileUpdate = (e: Event) => {
      const detail = (e as CustomEvent<Partial<User>>).detail;
      if (!detail) return;
      setUserRaw(prev => {
        if (!prev) return prev;
        const merged = { ...prev, ...detail };
        localStorage.setItem('user', JSON.stringify(merged));
        return normalizeUser(merged);
      });
    };
    window.addEventListener('esgcaffe:user-profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('esgcaffe:user-profile-updated', handleProfileUpdate);
  }, []);

  // Session heartbeat: poll every 60 s while authenticated.
  // When vendor admin revokes access, the backend sets isActive=false.
  // The next heartbeat call returns 401, the axios interceptor clears the
  // token and redirects to /login — the user is logged out within 60 s.
  // It also re-persists rolePermissions/allowedMenus so a role edit made by
  // a tenant-admin (e.g. toggling a feature off) reaches already-logged-in
  // users within 60 s instead of requiring them to log out and back in —
  // rolePermissions was otherwise only ever fetched once, at login.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(async () => {
      try {
        const response = await api.getProfile();
        if (response.isSuccess && response.data) {
          localStorage.setItem('user', JSON.stringify(response.data));
          setUser(response.data);
          // Federated MFEs run their own separate AuthProvider instance and only
          // read localStorage.user once, on mount — tell them to re-read it so a
          // role/permission edit doesn't require the affected user to re-login.
          window.dispatchEvent(new CustomEvent('esgcaffe:user-permissions-refreshed'));
        }
      } catch {
        // 401 is handled by the axios response interceptor (clears token + redirects to /login).
        // Any other error (network blip) is silently ignored so we don't log out on bad Wi-Fi.
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [user]);

  const loadUser = async () => {
    try {
      const response = await api.getProfile();
      if (response.isSuccess && response.data) {
        // Workspace enforcement on every authenticated load: if the user is on
        // the wrong host (the generic `saas` host, or no/incorrect subdomain),
        // redirect to their org's subdomain so the app is never used on `saas`.
        if (maybeRedirectToTenantSubdomain(response.data.company?.subdomain)) {
          return; // navigation in progress — halt before rendering the app
        }
        // Persist the full profile (incl. allowedMenus) so federated MFEs, which
        // read `localStorage.user`, get plan-based menu/feature gating. The login
        // response has no allowedMenus — only /auth/profile builds it.
        localStorage.setItem('user', JSON.stringify(response.data));
        setUser(response.data);
      } else {
        // Only force logout when the token in storage was actually rejected.
        // A null response with the token still present is almost always a
        // transient error — don't nuke the session for a network blip.
        if (!localStorage.getItem('token')) api.logout();
      }
    } catch (error) {
      // Same rule as above: if we still have a token, the profile fetch was
      // most likely a race (the request went out before localStorage.token
      // was populated) or a transient network error. Only logout when the
      // token is really gone — that means the server or another handler
      // already invalidated it.
      const isRealAuthFailure = !localStorage.getItem('token');
      console.error(
        'Failed to load user profile:',
        error,
        `— ${isRealAuthFailure ? 'no token in storage, logging out' : 'token still present, ignoring transient error'}`,
      );
      if (isRealAuthFailure) api.logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<LoginStepResult> => {
    const response = await api.login(email, password);
    if (!response.isSuccess) {
      throwFailedAuth(response, 'Invalid credentials');
    }
    if (!response.data) {
      throwFailedAuth(response, 'Invalid credentials');
    }
    if ('requiresOtp' in response.data && response.data.requiresOtp && response.data.challengeId) {
      return { requiresOtp: true, challengeId: response.data.challengeId };
    }
    if ('accessToken' in response.data && response.data.accessToken) {
      const profileResponse = await api.getProfile();
      const profile = profileResponse.isSuccess && profileResponse.data
        ? profileResponse.data
        : ('user' in response.data ? response.data.user : null);

      // OTP-disabled orgs skip the verify step, so apply the same workspace
      // redirect here: send the user to their org's subdomain after auth.
      if (profile && maybeRedirectToTenantSubdomain(profile.company?.subdomain)) {
        return { requiresOtp: false };
      }
      if (profile) {
        // Overwrite the login-stored user with the full profile so MFEs read
        // allowedMenus from localStorage (login response has none).
        localStorage.setItem('user', JSON.stringify(profile));
        setUser(profile);
      }
      return { requiresOtp: false };
    }
    throwFailedAuth(response, 'Invalid credentials');
  };

  const verifyLoginOtp = async (challengeId: string, otp: string) => {
    console.log('[verifyLoginOtp] Step 1: calling verify-login-otp API');
    const response = await api.verifyLoginOtp(challengeId, otp);
    console.log('[verifyLoginOtp] Step 2: OTP API response', { isSuccess: response.isSuccess, hasAccessToken: !!response.data?.accessToken, statusCode: response.statusCode });
    if (!response.isSuccess) {
      throwFailedAuth(response, 'Invalid or expired OTP');
    }
    if (!response.data?.accessToken) {
      throwFailedAuth(response, 'Invalid or expired OTP');
    }
    console.log('[verifyLoginOtp] Step 3: OTP verified, fetching profile');
    const profileResponse = await api.getProfile();
    console.log('[verifyLoginOtp] Step 4: profile response', { isSuccess: profileResponse.isSuccess, hasData: !!profileResponse.data, subdomain: profileResponse.data?.company?.subdomain });
    const profile = profileResponse.isSuccess ? profileResponse.data : (response.data.user ?? null);
    console.log('[verifyLoginOtp] Step 5: profile resolved', { hasProfile: !!profile, subdomain: profile?.company?.subdomain });

    // If the user's company has a subdomain and we're not already on it,
    // redirect to their workspace URL. The token is passed via URL fragment.
    if (profile && maybeRedirectToTenantSubdomain(profile.company?.subdomain)) {
      // Navigation in progress — halt here so we don't flash the dashboard first.
      console.log('[verifyLoginOtp] Step 6: subdomain redirect in progress, halting');
      return;
    }

    if (profile) {
      // Overwrite the login-stored user with the full profile so MFEs read
      // allowedMenus from localStorage (verify-otp response has none).
      localStorage.setItem('user', JSON.stringify(profile));
      setUser(profile);
    } else {
      console.warn('[verifyLoginOtp] Step 6: profile is null — setUser NOT called, isAuthenticated stays false!');
    }

    // Expiring-soon warning — backend attaches subscriptionWarning when
    // daysRemaining <= 7. Persist so a banner in any MFE can pick it up.
    const warning = (response.data as { subscriptionWarning?: { daysRemaining: number; endDate: string | null } }).subscriptionWarning;
    if (warning) {
      localStorage.setItem('subscriptionWarning', JSON.stringify(warning));
    } else {
      localStorage.removeItem('subscriptionWarning');
    }
  };

  const register = async (data: RegisterData) => {
    const response = await api.register(data);
    if (response.isSuccess && response.data) {
      setUser(response.data.user);
    } else {
      throw new Error(response.message || 'Registration failed');
    }
  };

  const logout = () => {
    api.logout();
    setUser(null);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  // Check if this user was invited by another user (tenant-admin invited this user)
  // Invited users have invitedBy set (someone invited them)
  const isInvitedUser = (): boolean => {
    return !!user?.invitedBy;
  };

  // Check if user has permission for a menu path
  // For invited users: check rolePermissions
  // For tenant-admin (not invited): show all menus based on subscription
  // Executive = platform-wide view-only role (sees everything, writes nothing).
  const isExecutiveRole = (role?: string): boolean =>
    (role ?? '').trim().toLowerCase() === 'executive';
  // A feature key is a write/action (blocked for Executive) when any word token is a mutating verb.
  const isExecutiveWriteFeature = (featureKey: string): boolean =>
    /(?:^|[^a-z])(create|add|new|edit|update|modify|delete|remove|destroy|submit|assign|unassign|reassign|save|upload|invite|generate|recalculate|seed|share|archive|unarchive|approve|reject|import|publish|send|resend|replace|sync|activate|deactivate|enable|disable|manage|toggle|run|duplicate|move|finalize|lock|unlock)(?:[^a-z]|$)/.test(
      (featureKey || '').toLowerCase(),
    );

  const hasMenuPermission = (menuPath: string): boolean => {
    if (!user) return false;
    if (isExecutiveRole(user.role)) return true;

    // Standalone routes — always allowed for all users
    const standaloneRoutes = ['/api-documentation', '/help_support/api-docs'];
    if (standaloneRoutes.some(r => menuPath.toLowerCase().startsWith(r))) {
      return true;
    }

    // If user is not an invited user (is tenant-admin), they have full access
    if (!isInvitedUser()) {
      return true;
    }

    // For invited users, check role permissions
    const rolePermissions = user.rolePermissions;
    if (!rolePermissions) return false;

    // Check for fullAccess (Admin role)
    if (rolePermissions.fullAccess) {
      return true;
    }

    // Path to permission key mapping (for paths that don't match permission keys directly)
    const pathToKeyMapping: Record<string, string[]> = {
      '/admin/publisher': ['administration', 'Administration', 'admin_publisher', 'publisher'],
      '/administration': ['administration', 'Administration'],
      '/dashboard': ['dashboard', 'Dashboard'],
      '/analytics': ['analytics', 'Analytics', 'overview', 'Overview'],
      '/analytics/overview': ['overview', 'Overview', 'analytics'],
      // Root analytics menu items
      '/environmental': ['environmental', 'Environmental', 'analytics'],
      '/social': ['social', 'Social', 'analytics'],
      '/governance': ['governance', 'Governance', 'analytics'],
      '/supply-chain': ['supply_chain', 'Supply Chain', 'supply-chain', 'analytics'],
      '/overview': ['overview', 'Overview', 'analytics'],
      
'/environmental/carbon_accounting': ['environmental', 'Environmental', 'carbon_accounting'],

      '/environmental/carbon': ['environmental', 'Environmental', 'carbon_accounting'],
      '/environmental/energy': ['environmental', 'Environmental', 'energy'],
      '/environmental/water': ['environmental', 'Environmental', 'water'],
      '/environmental/waste': ['environmental', 'Environmental', 'waste'],
      '/environmental/biodiversity': ['environmental', 'Environmental', 'biodiversity'],
      '/social/workforce': ['social', 'Social', 'workforce'],
      '/social/dei': ['social', 'Social', 'dei'],
      '/social/health-safety': ['social', 'Social', 'health_safety'],
      '/social/training': ['social', 'Social', 'training'],
      '/social/human-rights': ['social', 'Social', 'human_rights'],
      '/social/community': ['social', 'Social', 'community'],
      '/governance/board': ['governance', 'Governance', 'board'],
      '/governance/compensation': ['governance', 'Governance', 'compensation'],
      '/governance/ethics': ['governance', 'Governance', 'ethics'],
      '/governance/risk': ['governance', 'Governance', 'risk'],
      '/governance/policies': ['governance', 'Governance', 'policies'],
      '/supply-chain/suppliers': ['supply_chain', 'Supply Chain', 'suppliers'],
      '/supply-chain/risk': ['supply_chain', 'Supply Chain', 'risk_assessment'],
      '/supply-chain/assessments': ['supply_chain', 'Supply Chain', 'assessments'],
      '/supply-chain/scope3': ['supply_chain', 'Supply Chain', 'scope_3_data'],
      '/targets': ['actions', 'Actions', 'targets'],
      '/reporting/generation': ['reporting', 'Reporting', 'generation', 'Generation'],
      '/reporting/filings': ['reporting', 'Reporting', 'filings', 'Filings'],
      // Review Intelligence: the URL is hyphenated (/reporting/review-and-feedback)
      // but the DB menu path (and thus the granted role key) is de-hyphenated
      // (reporting_reviewandfeedback). Map the URL to that key so invited users
      // aren't wrongly denied by ProtectedRoute.
      '/reporting/review-and-feedback': ['reporting_reviewandfeedback', 'reviewandfeedback', 'review_and_feedback', 'review-and-feedback'],
      '/reporting/reviewandfeedback': ['reporting_reviewandfeedback', 'reviewandfeedback', 'review_and_feedback'],
      '/data_collection': ['data_collection', 'Data Collection'],
      '/data_collection/carbon_emission': ['data_collection', 'Data Collection', 'carbon_emission'],
      '/data_collection/carbon_emission/target_data': ['data_collection', 'Data Collection', 'target_data'],
      '/data_collection/carbon_emission/target_data/fillings': ['data_collection', 'Data Collection', 'fillings', 'Fillings'],
      '/data_reviews': ['data_reviews', 'Data Reviews', 'reviews_listing'],
      '/data_reviews/reviews_listing': ['data_reviews', 'Data Reviews', 'reviews_listing'],
      '/communication': ['communication', 'Communication', 'chart_and_esgai'],
      '/chat': ['communication', 'Communication', 'chart_and_esgai'],
      '/settings/company_profile': ['settings', 'Settings', 'company_profile'],
      '/settings/company_profie': ['settings', 'Settings', 'company_profile'],
      '/settings/team': ['settings', 'Settings', 'org_setup', 'term_permission', 'team_permissions'],
      '/settings/company_profie/term_permission': ['settings', 'Settings', 'term_permission'],
      '/settings/org_setup': ['settings', 'Settings', 'org_setup', 'term_permission'],
      '/settings/integrations': ['settings', 'Settings', 'integrations'],
      '/collections': ['collections', 'Collections', 'documents'],
      '/documents': ['documents', 'Documents', 'collections'],
    };

    const normalizedPath = menuPath.toLowerCase();

    // Check path mapping first
    const possibleKeys = pathToKeyMapping[menuPath] || pathToKeyMapping[normalizedPath];
    if (possibleKeys) {
      for (const key of possibleKeys) {
        if (rolePermissions[key]) {
          console.log(`[hasMenuPermission] ALLOWED: Found "${key}" for path "${menuPath}"`);
          return true;
        }
      }
    }

    // Convert menu path to key format (e.g., "/settings/company-profile" -> "settings_company_profile").
    // Lowercase so inconsistent catalog paths (e.g. "/Suppliers" with a capital
    // S) still match role keys, which are always stored lowercase.
    const menuKey = menuPath.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_').toLowerCase();

    // Check if this menu key exists in permissions
    // Permissions structure: { menuKey: { featureKey: { label, children } } }
    if (rolePermissions[menuKey]) {
      console.log(`[hasMenuPermission] ALLOWED: Found direct key "${menuKey}"`);
      return true;
    }

    // Also check ancestor menus by walking every path-segment prefix, from
    // most specific to least — e.g. for "/pcf/governance/review-queue":
    // "pcf_governance" then "pcf". A 3+ level route (module/section/tab —
    // every PCF v2 IA section index-redirects into a default tab, e.g.
    // "/pcf/governance" -> "/pcf/governance/review-queue") was previously
    // only ever checked against its FIRST segment ("pcf"), which is never
    // itself a granted permission key — only the section-level key
    // ("pcf_governance") is. That mismatch bounced every PCF-restricted
    // invited user back to Settings the instant a section's index route
    // redirected into its default tab.
    const segments = menuPath.replace(/^\//, '').split('/').filter(Boolean);
    for (let i = segments.length - 1; i >= 1; i--) {
      const ancestorKey = segments.slice(0, i).join('_').replace(/-/g, '_').toLowerCase();
      if (rolePermissions[ancestorKey]) {
        console.log(`[hasMenuPermission] ALLOWED: Found ancestor key "${ancestorKey}" for "${menuKey}"`);
        return true;
      }
    }

    // Grouped navigation (CBAM / dMRV): a page that used to be its own menu row
    // (/cbam/imports → key cbam_imports) now lives as a tab under a group row
    // (/cbam/trade/imports). Roles saved before the grouping still carry the
    // page-level key, so accept module_page for module/group/page URLs.
    if (segments.length >= 3) {
      const legacyKey = `${segments[0]}_${segments[2]}`.replace(/-/g, '_').toLowerCase();
      if (rolePermissions[legacyKey]) {
        console.log(`[hasMenuPermission] ALLOWED: Found legacy page key "${legacyKey}" for "${menuKey}"`);
        return true;
      }
    }

    console.log(`[hasMenuPermission] DENIED: No permission found for path "${menuPath}" (key: "${menuKey}")`);
    return false;
  };

  // Helper function to check feature permission for a specific menu key
  const checkFeatureInMenu = (
    menuKey: string,
    normalizedFeatureKey: string,
    rolePermissions: RolePermissions
  ): boolean => {
    const menuPermissions = rolePermissions[menuKey] as { label?: string; children?: Record<string, string> } | undefined;

    if (menuPermissions && typeof menuPermissions === 'object') {
      console.log(`[hasFeaturePermission] Found menuPermissions for "${menuKey}":`, menuPermissions);

      // New format: { menuKey: { label: "Label", children: { featureKey: "Feature Label" } } }
      if (menuPermissions.children) {
        // Check if featureKey exists directly in children
        if (normalizedFeatureKey in menuPermissions.children) {
          console.log(`[hasFeaturePermission] ALLOWED: Found "${normalizedFeatureKey}" in children of "${menuKey}"`);
          return true;
        }

        // Also check for partial match (e.g., "team_members" matches "team")
        for (const childKey of Object.keys(menuPermissions.children)) {
          if (childKey.toLowerCase() === normalizedFeatureKey ||
              normalizedFeatureKey.includes(childKey.toLowerCase()) ||
              childKey.toLowerCase().includes(normalizedFeatureKey)) {
            console.log(`[hasFeaturePermission] ALLOWED: Partial match "${childKey}" with "${normalizedFeatureKey}" in "${menuKey}"`);
            return true;
          }
        }
      }

      // Also check if featureKey exists as a direct key (old format support)
      if (normalizedFeatureKey in menuPermissions) {
        console.log(`[hasFeaturePermission] ALLOWED: Found "${normalizedFeatureKey}" as direct key in "${menuKey}"`);
        return true;
      }
    }
    return false;
  };

  // Check if user has permission for a specific feature within a menu
  // menuPath: The menu path (e.g., "/settings/company_profie/term_permission")
  // featureKey: The feature key to check (e.g., "team_members", "roles", "pending_invites")
  const hasFeaturePermission = (menuPath: string, featureKey: string): boolean => {
    if (!user) return false;
    // Executive: can view every feature/section, but never write/act.
    if (isExecutiveRole(user.role)) return !isExecutiveWriteFeature(featureKey);

    // If user is not an invited user (is tenant-admin), they have full access
    if (!isInvitedUser()) {
      return true;
    }

    const rolePermissions = user.rolePermissions;
    if (!rolePermissions) return false;

    // Check for fullAccess (Admin role)
    if (rolePermissions.fullAccess) {
      return true;
    }

    // Convert menu path to key format
    // e.g., "/settings/company_profie/term_permission" -> "settings_company_profie_term_permission"
    const menuKey = menuPath.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_').toLowerCase();

    // Normalize feature key
    const normalizedFeatureKey = featureKey.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');

    console.log(`[hasFeaturePermission] Checking: menuKey="${menuKey}", featureKey="${normalizedFeatureKey}"`);
    console.log(`[hasFeaturePermission] rolePermissions keys:`, Object.keys(rolePermissions));

    // First, check the exact menu key
    if (checkFeatureInMenu(menuKey, normalizedFeatureKey, rolePermissions)) {
      return true;
    }

    // If not found, check all permission keys that might be related to this page
    // This handles cases where the page is accessible via multiple routes (e.g., team_permissions vs company_profie_term_permission)
    // We check if any permission key contains relevant keywords from the current path
    const pathParts = menuKey.split('_').filter(p => p.length > 0);

    for (const permKey of Object.keys(rolePermissions)) {
      // Skip if we already checked this key or if it's not an object
      if (permKey === menuKey) continue;
      const permValue = rolePermissions[permKey];
      if (!permValue || typeof permValue !== 'object') continue;

      // Check if the permission key shares significant parts with our menu path
      // e.g., "settings_company_profie_term_permission" should also match for "settings_team_permissions"
      // by checking if they are both under "settings" and relate to "team" or "permission"
      const permParts = permKey.split('_').filter(p => p.length > 0);

      // Check for Team & Permission / Org Setup related paths specifically
      const isTeamPermissionRelated =
        (menuKey.includes('team') && menuKey.includes('permission')) ||
        (menuKey.includes('term_permission')) ||
        (menuKey.includes('org_setup'));
      const permIsTeamPermissionRelated =
        (permKey.includes('team') && permKey.includes('permission')) ||
        (permKey.includes('term_permission')) ||
        (permKey.includes('org_setup'));

      // If both are team/permission related and under same parent (settings), check this key too
      if (isTeamPermissionRelated && permIsTeamPermissionRelated) {
        if (pathParts[0] === permParts[0]) { // Same parent section (e.g., "settings")
          console.log(`[hasFeaturePermission] Checking alternative team/permission key: "${permKey}"`);
          if (checkFeatureInMenu(permKey, normalizedFeatureKey, rolePermissions)) {
            return true;
          }
        }
      }
    }

    console.log(`[hasFeaturePermission] DENIED: Feature "${normalizedFeatureKey}" not found in menu "${menuKey}" or related keys`);
    return false;
  };

  // Get all allowed menus from the pricing plan
  const getAllowedMenus = (): AllowedMenu[] => {
    return user?.allowedMenus || [];
  };

  // Helper to flatten all menu paths from allowedMenus hierarchy
  const getAllAllowedPaths = (menus: AllowedMenu[]): string[] => {
    const paths: string[] = [];
    const traverse = (menuList: AllowedMenu[]) => {
      for (const menu of menuList) {
        if (menu.path) {
          paths.push(menu.path.toLowerCase());
        }
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children);
        }
      }
    };
    traverse(menus);
    return paths;
  };

  // Helper to check if a path is allowed by the pricing plan (internal use)
  const checkPlanAllowed = (path: string): boolean => {
    if (!user) return false;

    // If no allowedMenus data, allow all (fallback for compatibility)
    if (!user.allowedMenus || user.allowedMenus.length === 0) {
      return true;
    }

    const normalizedPath = path.toLowerCase();
    const allowedPaths = getAllAllowedPaths(user.allowedMenus);

    // Check exact match
    if (allowedPaths.includes(normalizedPath)) {
      return true;
    }

    // Check if this path is a child of an allowed path
    for (const allowedPath of allowedPaths) {
      if (normalizedPath.startsWith(allowedPath + '/')) {
        return true;
      }
    }

    return false;
  };

  // Get the first allowed menu path for the user (for redirecting when accessing disallowed pages)
  const getFirstAllowedMenuPath = (): string => {
    if (!user) return '/dashboard';

    // Priority order for all users
    const menuPriorityOrder: Array<{ keys: string[]; path: string }> = [
      { keys: ['dashboard', 'Dashboard'], path: '/dashboard' },
      { keys: ['overview', 'Overview', 'analytics_overview', 'materiality'], path: '/analytics/overview' },
      { keys: ['analytics', 'Analytics'], path: '/analytics/overview' },
      { keys: ['environmental', 'Environmental', 'carbon_accounting'], path: '/environmental/carbon_accounting' },
      { keys: ['social', 'Social', 'workforce'], path: '/social/workforce' },
      { keys: ['governance', 'Governance', 'board'], path: '/governance/board' },
      { keys: ['supply_chain', 'Supply Chain', 'suppliers'], path: '/supply-chain/suppliers' },
      { keys: ['actions', 'Actions', 'targets'], path: '/targets' },
      { keys: ['reporting', 'Reporting', 'generation', 'Generation'], path: '/reporting/generation' },
      { keys: ['reporting_reviewandfeedback', 'reviewandfeedback', 'review-and-feedback', 'review_and_feedback'], path: '/reporting/review-and-feedback' },
      { keys: ['data_collection', 'Data Collection', 'carbon_emission', 'carbon_emissions'], path: '/data_collection/carbon_emission' },
      { keys: ['data_collection_fillings', 'fillings'], path: '/data_collection/fillings' },
      { keys: ['data_reviews', 'Data Reviews', 'reviews_listing'], path: '/data_reviews' },
      { keys: ['communication', 'Communication', 'chart_and_esgai'], path: '/communication' },
      { keys: ['administration', 'Administration'], path: '/administration' },
      { keys: ['settings', 'Settings', 'company_profile'], path: '/settings/company_profile' },
      { keys: ['collections', 'Collections'], path: '/collections' },
      { keys: ['documents', 'Documents'], path: '/documents' },
    ];

    // For tenant-admin (not invited), check plan permissions only
    if (!isInvitedUser()) {
      // Find first menu allowed by plan
      for (const { path } of menuPriorityOrder) {
        if (checkPlanAllowed(path)) {
          console.log(`[getFirstAllowedMenuPath] Tenant-admin: Found allowed menu by plan: ${path}`);
          return path;
        }
      }

      // If no match in priority order, check actual allowed paths from plan
      if (user.allowedMenus && user.allowedMenus.length > 0) {
        const allowedPaths = getAllAllowedPaths(user.allowedMenus);
        if (allowedPaths.length > 0) {
          console.log(`[getFirstAllowedMenuPath] Tenant-admin: Using first allowed path from plan: ${allowedPaths[0]}`);
          return allowedPaths[0];
        }
      }

      // Fallback to dashboard if no plan data
      return '/dashboard';
    }

    // For invited users, check both plan AND role permissions
    const rolePermissions = user.rolePermissions;

    // If no rolePermissions, try to get first path from plan
    if (!rolePermissions) {
      if (user.allowedMenus && user.allowedMenus.length > 0) {
        const allowedPaths = getAllAllowedPaths(user.allowedMenus);
        if (allowedPaths.length > 0) {
          console.log(`[getFirstAllowedMenuPath] No rolePermissions, using first allowed path from plan: ${allowedPaths[0]}`);
          return allowedPaths[0];
        }
      }
      return '/dashboard'; // No permissions data yet — land on dashboard, not login
    }

    // Check for fullAccess (Admin role)
    if (rolePermissions.fullAccess) {
      // Still need to check plan permissions
      for (const { path } of menuPriorityOrder) {
        if (checkPlanAllowed(path)) {
          console.log(`[getFirstAllowedMenuPath] Admin role: Found allowed menu by plan: ${path}`);
          return path;
        }
      }

      // If no match in priority order, check actual allowed paths
      if (user.allowedMenus && user.allowedMenus.length > 0) {
        const allowedPaths = getAllAllowedPaths(user.allowedMenus);
        if (allowedPaths.length > 0) {
          return allowedPaths[0];
        }
      }
      return '/dashboard';
    }

    // Log all rolePermissions keys for debugging
    console.log('[getFirstAllowedMenuPath] All rolePermission keys:', Object.keys(rolePermissions));

    // Check each menu in priority order - must be allowed by BOTH plan AND role
    for (const { keys, path } of menuPriorityOrder) {
      // First check if plan allows this path
      if (!checkPlanAllowed(path)) {
        continue;
      }

      // Then check if role allows this path
      for (const key of keys) {
        if (rolePermissions[key]) {
          console.log(`[getFirstAllowedMenuPath] Found allowed menu: ${key} -> ${path}`);
          return path;
        }
      }
    }

    // If no match in priority order, find first path that is allowed by both plan AND role
    if (user.allowedMenus && user.allowedMenus.length > 0) {
      const allowedPaths = getAllAllowedPaths(user.allowedMenus);
      console.log('[getFirstAllowedMenuPath] Checking allowed paths from plan:', allowedPaths);

      for (const path of allowedPaths) {
        // Convert path to permission key format (e.g., '/data_collection/fillings' -> 'data_collection_fillings')
        const permissionKey = path.replace(/^\//, '').replace(/\//g, '_').toLowerCase();

        if (rolePermissions[permissionKey]) {
          console.log(`[getFirstAllowedMenuPath] Found allowed menu from plan paths: ${permissionKey} -> ${path}`);
          return path;
        }

        // Also check without parent prefix (e.g., 'fillings' instead of 'data_collection_fillings')
        const lastPart = permissionKey.split('_').pop();
        if (lastPart && rolePermissions[lastPart]) {
          console.log(`[getFirstAllowedMenuPath] Found allowed menu by last part: ${lastPart} -> ${path}`);
          return path;
        }
      }
    }

    // If still no match, return first allowed path from plan (user has limited permissions)
    if (user.allowedMenus && user.allowedMenus.length > 0) {
      const allowedPaths = getAllAllowedPaths(user.allowedMenus);
      if (allowedPaths.length > 0) {
        // Check if user has permission for any of these paths
        for (const path of allowedPaths) {
          if (hasMenuPermission(path)) {
            console.log(`[getFirstAllowedMenuPath] Found allowed path via hasMenuPermission: ${path}`);
            return path;
          }
        }
        // Role has no permissions matching plan menus — land on the first plan-allowed
        // page so the user stays inside the app (not bounced to the login form).
        console.log('[getFirstAllowedMenuPath] No role-matching menu found, landing on first plan path:', allowedPaths[0]);
        return allowedPaths[0];
      }
    }

    // Absolute last resort: dashboard (never return /login — that creates a redirect loop
    // when the user IS authenticated but has no matching permissions).
    console.log('[getFirstAllowedMenuPath] No allowed menu found, falling back to /dashboard');
    return '/dashboard';
  };

  // Check if a menu path is allowed by the pricing plan
  // isParentSection: Set to true when checking if a parent menu section should be visible
  //                  (parent sections show if ANY of their children are allowed)
  //                  Set to false/undefined for actual menu items (must be explicitly allowed)
  const isMenuAllowedByPlan = (menuPath: string, isParentSection: boolean = false): boolean => {
    if (!user) return false;

    // Standalone routes — always allowed, no DB menu entry needed
    const standaloneRoutes = ['/api-documentation', '/help_support/api-docs'];
    if (standaloneRoutes.some(r => menuPath.toLowerCase().startsWith(r))) {
      return true;
    }

    // If no allowedMenus data, allow all (fallback for compatibility)
    if (!user.allowedMenus || user.allowedMenus.length === 0) {
      console.log(`[isMenuAllowedByPlan] No allowedMenus data, allowing all menus. Path: ${menuPath}`);
      return true;
    }

    const normalizedPath = menuPath.toLowerCase();
    const allowedPaths = getAllAllowedPaths(user.allowedMenus);

    // Debug log for first few checks to see what paths are allowed
    if (menuPath === '/dashboard') {
      console.log('[isMenuAllowedByPlan] Allowed paths from plan:', allowedPaths);
    }

    // Hardcoded Analytics bundle (Sidebar.tsx "hardcoded-analytics-*" entries):
    // Environmental/Social/Governance/Supply Chain are injected into the
    // sidebar purely client-side whenever Dashboard is plan-allowed — they
    // have NO corresponding row in the super-admin menus table. Without this
    // whitelist, clicking them "passes" isMenuAllowedByPlan=false and
    // ProtectedRoute immediately bounces the user back to /dashboard.
    const HARDCODED_ANALYTICS_BUNDLE_PATHS = [
      '/analytics/environmental',
      '/analytics/social',
      '/analytics/governance',
      '/analytics/supply_chain',
    ];
    if (HARDCODED_ANALYTICS_BUNDLE_PATHS.includes(normalizedPath)) {
      return allowedPaths.some(p => p === '/dashboard' || p.endsWith('/dashboard'));
    }

    // Check exact match - this path is explicitly in the allowed list
    if (allowedPaths.includes(normalizedPath)) {
      return true;
    }

    // The URL may use a hyphenated form (e.g. /reporting/review-and-feedback)
    // while the DB-stored menu path is /reporting/reviewandfeedback. Also try
    // the de-hyphenated variant so the canonical URL still passes the check.
    const deHyphenated = normalizedPath.replace(/-/g, '');
    if (deHyphenated !== normalizedPath && allowedPaths.includes(deHyphenated)) {
      return true;
    }

    // Check if this path is a child of an allowed path (parent was allowed, so children inherit)
    for (const allowedPath of allowedPaths) {
      if (normalizedPath.startsWith(allowedPath + '/')) {
        return true;
      }
      if (deHyphenated !== normalizedPath && deHyphenated.startsWith(allowedPath + '/')) {
        return true;
      }
    }

    // Only for parent sections: check if any child of this path is allowed
    // This allows the parent menu to show so user can navigate to the allowed children
    if (isParentSection) {
      for (const allowedPath of allowedPaths) {
        if (allowedPath.startsWith(normalizedPath + '/')) {
          return true;
        }
      }
    }

    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        verifyLoginOtp,
        register,
        logout,
        updateUser,
        hasMenuPermission,
        hasFeaturePermission,
        isInvitedUser,
        isMenuAllowedByPlan,
        getAllowedMenus,
        getFirstAllowedMenuPath,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
